import React from 'react';
import { 
  Trash2, 
  CheckCircle, 
  Clock, 
  AlertTriangle, 
  GripVertical, 
  Edit3, 
  User
} from 'lucide-react';
import Button from './Button';
import { ProductionTask } from '../../types';
import { formatCurrency } from '../../utils/formatters';
import { formatArgentinaDate } from '../../utils/dateUtils';
import '../../styles/kanban.css';

interface KanbanViewProps {
  tasks: ProductionTask[];
  onEditTask: (task: ProductionTask) => void;
  onDeleteTask: (id: string) => void;
  getSupplierName: (id: string | null) => string;
  onStatusChange: (taskId: string, newStatus: 'pending' | 'in_progress' | 'finished') => void;
}

const KanbanView: React.FC<KanbanViewProps> = ({
  tasks,
  onEditTask,
  onDeleteTask,
  getSupplierName,
  onStatusChange,
}) => {
  // Group tasks by status
  const pendingTasks = tasks.filter(task => task.status === 'pending');
  const inProgressTasks = tasks.filter(task => task.status === 'in_progress');
  const finishedTasks = tasks.filter(task => task.status === 'finished');

  // Drag and drop handlers with improved visual feedback
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, task: ProductionTask) => {
    e.dataTransfer.setData('taskId', task.id);
    e.dataTransfer.effectAllowed = 'move';
    
    // Add visual feedback to dragged item
    e.currentTarget.classList.add('opacity-60', 'scale-105', 'rotate-2', 'shadow-2xl');
    
    // Add visual feedback to all columns
    const columns = document.querySelectorAll('[data-kanban-column]');
    columns.forEach(column => {
      if (column && column.classList) {
        column.classList.add('transition-all', 'duration-200');
      }
    });
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    // Add enhanced visual feedback
    if (e.currentTarget && e.currentTarget.classList) {
      e.currentTarget.classList.add('bg-blue-50', 'dark:bg-blue-900/20', 'border-blue-300', 'dark:border-blue-600', 'scale-105');
      
      // Add glow effect
      e.currentTarget.style.boxShadow = '0 0 20px rgba(59, 130, 246, 0.3)';
    }
  };
  
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    // Remove visual feedback
    if (e.currentTarget && e.currentTarget.classList) {
      e.currentTarget.classList.remove('bg-blue-50', 'dark:bg-blue-900/20', 'border-blue-300', 'dark:border-blue-600', 'scale-105');
      e.currentTarget.style.boxShadow = '';
    }
  };
  
  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    // Remove all visual feedback from dragged item
    if (e.currentTarget && e.currentTarget.classList) {
      e.currentTarget.classList.remove('opacity-60', 'scale-105', 'rotate-2', 'shadow-2xl');
    }
    
    // Remove visual feedback from all columns
    const columns = document.querySelectorAll('[data-kanban-column]');
    columns.forEach(column => {
      if (column && column.classList) {
        column.classList.remove('bg-blue-50', 'dark:bg-blue-900/20', 'border-blue-300', 'dark:border-blue-600', 'scale-105');
        (column as HTMLElement).style.boxShadow = '';
      }
    });
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, newStatus: 'pending' | 'in_progress' | 'finished') => {
    e.preventDefault();
    
    // Remove all visual feedback
    if (e.currentTarget && e.currentTarget.classList) {
      e.currentTarget.classList.remove('bg-blue-50', 'dark:bg-blue-900/20', 'border-blue-300', 'dark:border-blue-600', 'scale-105');
      e.currentTarget.style.boxShadow = '';
      
      const taskId = e.dataTransfer.getData('taskId');
      if (taskId) {
        // Add success feedback
        e.currentTarget.classList.add('bg-green-50', 'dark:bg-green-900/20', 'border-green-300', 'dark:border-green-600');
        setTimeout(() => {
          if (e.currentTarget && e.currentTarget.classList) {
            e.currentTarget.classList.remove('bg-green-50', 'dark:bg-green-900/20', 'border-green-300', 'dark:border-green-600');
          }
        }, 500);
        
        onStatusChange(taskId, newStatus);
      }
    }
  };

  // Function to render a task card with improved design
  const renderTask = (task: ProductionTask) => {
    const getStatusColor = (status: string) => {
      switch (status) {
        case 'pending':
          return {
            card: 'bg-white dark:bg-gray-800 border-amber-200 dark:border-amber-700 hover:border-amber-300 dark:hover:border-amber-600',
            accent: 'bg-amber-500',
            text: 'text-amber-700 dark:text-amber-300'
          };
        case 'in_progress':
          return {
            card: 'bg-white dark:bg-gray-800 border-blue-200 dark:border-blue-700 hover:border-blue-300 dark:hover:border-blue-600',
            accent: 'bg-blue-500',
            text: 'text-blue-700 dark:text-blue-300'
          };
        case 'finished':
          return {
            card: 'bg-white dark:bg-gray-800 border-green-200 dark:border-green-700 hover:border-green-300 dark:hover:border-green-600',
            accent: 'bg-green-500',
            text: 'text-green-700 dark:text-green-300'
          };
        default:
          return {
            card: 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700',
            accent: 'bg-gray-500',
            text: 'text-gray-700 dark:text-gray-300'
          };
      }
    };

    const colors = getStatusColor(task.status);

    return (
      <div 
        key={task.id} 
        draggable
        onDragStart={(e) => handleDragStart(e, task)}
        onDragEnd={handleDragEnd}
        className={`
          group relative bg-white dark:bg-gray-800 rounded-lg border-2 shadow-sm hover:shadow-md 
          transition-all duration-200 cursor-pointer mb-2 overflow-hidden kanban-card
          ${colors.card}
        `}
        onClick={() => onEditTask(task)}
      >
        {/* Status indicator bar */}
        <div className={`absolute top-0 left-0 right-0 h-1 ${colors.accent}`} />
        
        {/* Drag handle */}
        <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <GripVertical className="h-4 w-4 text-gray-400 cursor-move" />
        </div>

        <div className="p-3 pt-4">
          {/* Task title */}
          <div className="flex items-start justify-between mb-2">
            <h4 className="font-medium text-gray-900 dark:text-white text-sm leading-tight pr-6">
              {task.task_name}
            </h4>
            <div className="flex items-center space-x-1 kanban-card-actions">
              <Button
                variant="ghost"
                size="sm"
                icon={<Edit3 className="h-3 w-3" />}
                onClick={(e) => {
                  e.stopPropagation();
                  onEditTask(task);
                }}
                className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 p-1"
              >
              </Button>
              <Button
                variant="ghost"
                size="sm"
                icon={<Trash2 className="h-3 w-3" />}
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteTask(task.id);
                }}
                className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-red-500 hover:text-red-700 dark:hover:text-red-400 p-1"
              >
              </Button>
            </div>
          </div>

          {/* Supplier info */}
          <div className="flex items-center mb-2">
            <User className="h-3 w-3 text-gray-400 mr-1 flex-shrink-0" />
            <span className="text-xs text-gray-600 dark:text-gray-400 truncate">
              {getSupplierName(task.supplier_id)}
            </span>
          </div>

          {/* Task details - compact at bottom */}
          <div className="flex flex-col gap-2 text-xs">
            {/* Dates row */}
            <div className="flex items-center space-x-3">
              {/* Dates compact */}
              {task.start_date && (
                <span className="inline-flex items-center text-gray-600 dark:text-gray-400">
                  <Clock className="h-3 w-3 mr-1" />
                  {formatArgentinaDate(task.start_date)}
                </span>
              )}
              {task.end_date && (
                <span className="inline-flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  {formatArgentinaDate(task.end_date)}
                </span>
              )}
            </div>
            
            {/* Cost and Status row */}
            <div className="flex items-center justify-between">
              {/* Cost */}
              <span className="inline-flex items-center font-medium text-gray-900 dark:text-white">
                {formatCurrency(task.cost)}
              </span>
              
              {/* Status badge */}
              <div className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${colors.text} bg-opacity-10`}>
                {task.status === 'pending' ? 'Pendiente' : 
                 task.status === 'in_progress' ? 'En Proceso' : 'Terminado'}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Pending Column */}
      <div 
        data-kanban-column="pending"
        className="kanban-column-pending rounded-2xl border border-amber-200 dark:border-amber-800 p-4 transition-all duration-200 hover:shadow-lg"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, 'pending')}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="p-1.5 bg-amber-100 dark:bg-amber-900/30 rounded-md mr-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h3 className="font-bold text-base text-gray-900 dark:text-white">
                Pendiente
              </h3>
              <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                {pendingTasks.length} tareas
              </p>
            </div>
          </div>
          <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
        </div>
        
        <div className="space-y-2">
          {pendingTasks.map(renderTask)}
          {pendingTasks.length === 0 && (
            <div className="text-center py-6">
              <div className="w-12 h-12 mx-auto mb-3 bg-amber-100 dark:bg-amber-900/20 rounded-full flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-amber-400" />
              </div>
              <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">No hay tareas pendientes</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Las nuevas tareas aparecerán aquí</p>
            </div>
          )}
        </div>
      </div>

      {/* In Progress Column */}
      <div 
        data-kanban-column="in_progress"
        className="kanban-column-in-progress rounded-2xl border border-blue-200 dark:border-blue-800 p-4 transition-all duration-200 hover:shadow-lg"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, 'in_progress')}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-md mr-2">
              <Clock className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="font-bold text-base text-gray-900 dark:text-white">
                En Proceso
              </h3>
              <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                {inProgressTasks.length} tareas
              </p>
            </div>
          </div>
          <div className="w-3 h-3 bg-blue-500 rounded-full status-indicator"></div>
        </div>
        
        <div className="space-y-2">
          {inProgressTasks.map(renderTask)}
          {inProgressTasks.length === 0 && (
            <div className="text-center py-6">
              <div className="w-12 h-12 mx-auto mb-3 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
                <Clock className="h-6 w-6 text-blue-400" />
              </div>
              <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">No hay tareas en proceso</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Arrastra tareas aquí para comenzar</p>
            </div>
          )}
        </div>
      </div>

      {/* Finished Column */}
      <div 
        data-kanban-column="finished"
        className="kanban-column-finished rounded-2xl border border-green-200 dark:border-green-800 p-4 transition-all duration-200 hover:shadow-lg"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, 'finished')}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="p-1.5 bg-green-100 dark:bg-green-900/30 rounded-md mr-2">
              <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h3 className="font-bold text-base text-gray-900 dark:text-white">
                Terminado
              </h3>
              <p className="text-xs text-green-600 dark:text-green-400 font-medium">
                {finishedTasks.length} tareas
              </p>
            </div>
          </div>
          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
        </div>
        
        <div className="space-y-2">
          {finishedTasks.map(renderTask)}
          {finishedTasks.length === 0 && (
            <div className="text-center py-6">
              <div className="w-12 h-12 mx-auto mb-3 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-400" />
              </div>
              <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">No hay tareas terminadas</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Las tareas completadas aparecerán aquí</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default KanbanView;